/**
 * ADR-0013 — Linear lots indexing helpers.
 * Batch paths must index once (O(lots)) then look up O(1), never scan lots per product.
 */

import { startOfLocalDayMs, isLotAvailableForSale } from '../../../utils/lotSystem';

/**
 * @param {Array|object|null|undefined} lots
 * @returns {object[]}
 */
export function normalizeLotsList(lots) {
  if (Array.isArray(lots)) return lots;
  if (lots && typeof lots === 'object') return Object.values(lots);
  return [];
}

/**
 * Sellable/stock-display lot — same rule for Stock, Inventaire, Bilan value, POS.
 * SSOT body lives in lotSystem.isLotAvailableForSale (avoid duplicate predicates).
 * @param {object} lot
 * @param {Date|number} [referenceDate] — Date or startOfLocalDayMs
 */
export function isLotIncludedInStockMetrics(lot, referenceDate = new Date()) {
  return isLotAvailableForSale(lot, referenceDate);
}

export function lotProductKey(productId) {
  return String(productId ?? '');
}

export function lotLocationKey(location) {
  // Case-insensitive canonical key so 'Principal' and 'principal' collapse.
  const loc = String(location || 'Principal').trim().toLowerCase();
  return loc || 'principal';
}

export function lotProductLocationKey(productId, location) {
  return `${lotProductKey(productId)}|${lotLocationKey(location)}`;
}

/**
 * @param {Array|object|null|undefined} lots
 * @returns {Map<string, object[]>}
 */
export function indexLotsByProduct(lots) {
  const map = new Map();
  for (const lot of normalizeLotsList(lots)) {
    if (!lot || lot.productId == null) continue;
    const key = lotProductKey(lot.productId);
    let bucket = map.get(key);
    if (!bucket) {
      bucket = [];
      map.set(key, bucket);
    }
    bucket.push(lot);
  }
  return map;
}

/**
 * @param {Array|object|null|undefined} lots
 * @returns {Map<string, object[]>}
 */
export function indexLotsByProductLocation(lots) {
  const map = new Map();
  for (const lot of normalizeLotsList(lots)) {
    if (!lot || lot.productId == null) continue;
    const key = lotProductLocationKey(lot.productId, lot.location);
    let bucket = map.get(key);
    if (!bucket) {
      bucket = [];
      map.set(key, bucket);
    }
    bucket.push(lot);
  }
  return map;
}

/**
 * Aggregate FIFO stock metrics from a pre-filtered lot list (no rescans).
 * stockNow/value only from sellable lots (active, not expired) — aligned with POS.
 * @param {object[]} productLots
 * @param {Date|number} [referenceDate] — Date or startOfLocalDayMs (computed once per pass)
 */
export function aggregateLotsMetrics(productLots = [], referenceDate = new Date()) {
  const todayStartMs = startOfLocalDayMs(referenceDate);
  let stockIn = 0;
  let stockOut = 0;
  let stockNow = 0;
  let value = 0;
  let potentialProfit = 0;
  let activeLotsCount = 0;

  for (const lot of productLots) {
    if (!lot) continue;
    const total = Number(lot.totalQuantity) || 0;
    const rem = Number(lot.remainingQuantity) || 0;
    stockIn += total;
    stockOut += Math.max(0, total - rem);
    if (!isLotIncludedInStockMetrics(lot, todayStartMs)) continue;
    activeLotsCount += 1;
    const purchase = Number(lot.purchaseUnitPrice) || 0;
    const selling = Number(lot.sellingUnitPrice) || purchase;
    stockNow += rem;
    value += rem * purchase;
    potentialProfit += rem * (selling - purchase);
  }

  return {
    stockIn,
    stockOut,
    stockNow,
    value,
    potentialProfit,
    lotsCount: productLots.length,
    activeLotsCount
  };
}

/**
 * @param {Map<string, object[]>} index
 * @param {string|number} productId
 * @param {string|null} [location]
 * @returns {object[]}
 */
export function getLotsFromIndex(index, productId, location = null) {
  if (!index || productId == null) return [];
  if (location != null && location !== '') {
    return index.get(lotProductLocationKey(productId, location)) || [];
  }
  // Product-only index
  return index.get(lotProductKey(productId)) || [];
}
